import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "@vimaana/ui";

import { angebotFrageAnstossen } from "../lib/angebotFrage";
import type { Arbeitsplan, EintragenAntwort, EintragenBody, PlanBedarf } from "../lib/arbeitsplan";

/**
 * Arbeitsplan eines Projekts (Plan Projekt-Tracking, Etappe 6).
 *
 * Schlüssel unter ["projekt", id], damit Lage und Phase im Kopf mitziehen: jede
 * Änderung am Plan kann die Phase ändern („Tag 3 hat noch kein Datum").
 */
export function useArbeitsplan(projektId?: string) {
  return useQuery<Arbeitsplan>({
    queryKey: ["projekt", projektId, "arbeitsplan"],
    queryFn: () => apiGet<Arbeitsplan>(`/api/kontor/projekt/${projektId}/arbeitsplan`),
    enabled: !!projektId,
    staleTime: 10_000,
  });
}

function useNachziehen(projektId: string) {
  const qc = useQueryClient();
  return (plan?: Arbeitsplan) => {
    if (plan) qc.setQueryData(["projekt", projektId, "arbeitsplan"], plan);
    qc.invalidateQueries({ queryKey: ["projekt", projektId] });
    qc.invalidateQueries({ queryKey: ["projekte"] });
    qc.invalidateQueries({ queryKey: ["kalender"] });
  };
}

export type TagNeuBody = { titel?: string; notiz?: string; positionen?: string[]; packzettel_id?: string; nacharbeit?: boolean; nach_tag_id?: string };
export type TagPatchBody = Partial<{ titel: string; notiz: string; positionen: string[]; packzettel_id: string; nacharbeit: boolean }>;

export function useArbeitstagNeu(projektId: string) {
  const nach = useNachziehen(projektId);
  return useMutation({
    mutationFn: (b: TagNeuBody) => apiPost<{ id: string; plan: Arbeitsplan }>(`/api/kontor/projekt/${projektId}/arbeitstag`, b),
    onSuccess: (d) => nach(d.plan),
  });
}

export function useArbeitstagAendern(projektId: string) {
  const nach = useNachziehen(projektId);
  return useMutation({
    mutationFn: (v: { id: string; patch: TagPatchBody }) => apiPatch<Arbeitsplan>(`/api/kontor/arbeitstag/${v.id}`, v.patch),
    onSuccess: (plan) => nach(plan),
  });
}

export function useArbeitstagKopie(projektId: string) {
  const nach = useNachziehen(projektId);
  return useMutation({
    mutationFn: (id: string) => apiPost<{ id: string; plan: Arbeitsplan }>(`/api/kontor/arbeitstag/${id}/kopie`, {}),
    onSuccess: (d) => nach(d.plan),
  });
}

/** Etappe 7: eine Leistung des Tages (oder alle) abhaken — dieselbe Checkliste wie in der Team-App. */
export function useHaken(projektId: string) {
  const nach = useNachziehen(projektId);
  return useMutation({
    mutationFn: (v: { tagId: string; anker?: string; alle?: boolean; erledigt: boolean }) =>
      apiPost<Arbeitsplan>(`/api/kontor/arbeitstag/${v.tagId}/haken`, { anker: v.anker || "", alle: !!v.alle, erledigt: v.erledigt }),
    onSuccess: (plan) => nach(plan),
  });
}

/** Was an diesem Tag nicht abgehakt ist, wird ein Nacharbeitstag ohne Datum. */
export function useNacharbeit(projektId: string) {
  const nach = useNachziehen(projektId);
  return useMutation({
    mutationFn: (tagId: string) => apiPost<{ id: string; plan: Arbeitsplan }>(`/api/kontor/arbeitstag/${tagId}/nacharbeit`, {}),
    onSuccess: (d) => nach(d.plan),
  });
}

export function useArbeitstagLoeschen(projektId: string) {
  const nach = useNachziehen(projektId);
  return useMutation({
    mutationFn: (id: string) => apiDelete<Arbeitsplan>(`/api/kontor/arbeitstag/${id}`),
    onSuccess: (plan) => nach(plan),
  });
}

export function useReihenfolge(projektId: string) {
  const nach = useNachziehen(projektId);
  return useMutation({
    mutationFn: (ids: string[]) => apiPut<Arbeitsplan>(`/api/kontor/projekt/${projektId}/arbeitsplan/reihenfolge`, { ids }),
    onSuccess: (plan) => nach(plan),
  });
}

/** Erst Vorschau (`vorschau: true`, schreibt nichts), dann eintragen. */
export function useEintragen(projektId: string) {
  const nach = useNachziehen(projektId);
  return useMutation({
    mutationFn: (b: EintragenBody) => apiPost<EintragenAntwort>(`/api/kontor/projekt/${projektId}/arbeitsplan/eintragen`, b),
    onSuccess: (d) => {
      if (!d.eingetragen) return;
      nach(d.plan);
      angebotFrageAnstossen(projektId);
    },
  });
}

export function usePackzettelAusfuehrung(projektId: string) {
  const nach = useNachziehen(projektId);
  return useMutation({
    mutationFn: (v: { id: string; helfer_id: string; pauschale_id: string }) =>
      apiPatch<Arbeitsplan>(`/api/kontor/packzettel/${v.id}/ausfuehrung`, { helfer_id: v.helfer_id, pauschale_id: v.pauschale_id }),
    onSuccess: (plan) => nach(plan),
  });
}

/* „Was muss da sein" — dieselben Routen wie die Terminvorbereitung im Planer
   (`/kontor/event`), mit Art, Material und Mietende (Migration 195). */
export type BedarfNeuBody = {
  termin_id: string;
  titel: string;
  kategorie: string;
  details?: string;
  uhrzeit?: string;
  lieferant_id?: string;
  projekt_material_id?: string;
  bis_termin_id?: string;
};
export type BedarfPatchBody = Partial<{ titel: string; erledigt: boolean; uhrzeit: string; lieferant_id: string; projekt_material_id: string; bis_termin_id: string; kategorie: string }>;

export function useBedarfNeu(projektId: string) {
  const nach = useNachziehen(projektId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: BedarfNeuBody) => apiPost<PlanBedarf>("/api/kontor/event", b),
    onSuccess: () => { nach(); qc.invalidateQueries({ queryKey: ["events"] }); },
  });
}

export function useBedarfAendern(projektId: string) {
  const nach = useNachziehen(projektId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; patch: BedarfPatchBody }) => apiPatch<PlanBedarf>(`/api/kontor/event/${v.id}`, v.patch),
    onSuccess: () => { nach(); qc.invalidateQueries({ queryKey: ["events"] }); },
  });
}

export function useBedarfLoeschen(projektId: string) {
  const nach = useNachziehen(projektId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<{ ok: boolean }>(`/api/kontor/event/${id}`),
    onSuccess: () => { nach(); qc.invalidateQueries({ queryKey: ["events"] }); },
  });
}

/* Vorschläge aus dem Angebot (Etappe 6b) — Lieferung/Maschine an einem Tag. */
export function useVorschlagAnnehmen(projektId: string) {
  const nach = useNachziehen(projektId);
  return useMutation({
    mutationFn: (v: { tagId: string; pmId: string }) =>
      apiPost<Arbeitsplan>(`/api/kontor/arbeitstag/${v.tagId}/vorschlag`, { projekt_material_id: v.pmId }),
    onSuccess: (plan) => nach(plan),
  });
}

export function useVorschlaegeAlle(projektId: string) {
  const nach = useNachziehen(projektId);
  return useMutation({
    mutationFn: (tagId: string = "") => apiPost<Arbeitsplan>(`/api/kontor/projekt/${projektId}/arbeitsplan/vorschlaege`, { tag_id: tagId }),
    onSuccess: (plan) => nach(plan),
  });
}

export function useVorschlagAus(projektId: string) {
  const nach = useNachziehen(projektId);
  return useMutation({
    mutationFn: (v: { pmId: string; wieder?: boolean }) =>
      apiPost<Arbeitsplan>(`/api/kontor/projekt/${projektId}/arbeitsplan/vorschlag/${v.wieder ? "wieder" : "aus"}`, { projekt_material_id: v.pmId }),
    onSuccess: (plan) => nach(plan),
  });
}
